import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  TCHOMA_NAVIGATION_EVENT,
  createRouteState,
  normalizePathname,
  resolveInitialServiceWorkerStatus,
  resolveInstallCapability,
  routeForId,
  validateRoutes,
  type InstallPromptResult,
  type InstallState,
  type NavigationEventDetail,
  type NavigationKind,
  type RouteDefinition,
  type RouteState,
  type RuntimeDiagnostics,
  type ServiceWorkerState,
} from './core'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export interface ServiceWorkerOptions {
  enabled: boolean
  url?: string
}

export interface WebRuntimeState<RouteId extends string = string> {
  route: RouteState<RouteId>
  diagnostics: RuntimeDiagnostics
  install: InstallState
  serviceWorker: ServiceWorkerState
}

export interface WebRuntimeActions<RouteId extends string = string> {
  navigate: (routeId: RouteId) => void
  promptInstall: () => Promise<InstallPromptResult>
  checkForServiceWorkerUpdate: () => Promise<void>
  applyServiceWorkerUpdate: () => void
}

export interface WebRuntimeMeta<RouteId extends string = string> {
  routes: readonly RouteDefinition<RouteId>[]
}

export interface WebRuntimeContextValue<RouteId extends string = string> {
  state: WebRuntimeState<RouteId>
  actions: WebRuntimeActions<RouteId>
  meta: WebRuntimeMeta<RouteId>
}

export interface WebRuntimeProviderProps<RouteId extends string> {
  routes: readonly RouteDefinition<RouteId>[]
  fallbackRouteId: RouteId
  serviceWorker?: ServiceWorkerOptions
  children: ReactNode
}

const RuntimeContext = createContext<WebRuntimeContextValue<string> | null>(null)

function isStandalone(): boolean {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia('(display-mode: standalone)').matches || standaloneNavigator.standalone === true
}

function readDiagnostics(): RuntimeDiagnostics {
  return {
    secureContext: window.isSecureContext,
    standalone: isStandalone(),
    serviceWorkerSupported: 'serviceWorker' in navigator,
    online: navigator.onLine,
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown browser runtime error'
}

export function WebRuntimeProvider<RouteId extends string>({
  routes,
  fallbackRouteId,
  serviceWorker,
  children,
}: WebRuntimeProviderProps<RouteId>) {
  validateRoutes(routes, fallbackRouteId)

  const serviceWorkerEnabled = serviceWorker?.enabled ?? false
  const serviceWorkerUrl = serviceWorker?.url ?? '/sw.js'
  const initialDiagnostics = useMemo(readDiagnostics, [])
  const initialRoute = useMemo(
    () => createRouteState(routes, fallbackRouteId, window.location.pathname, 'initial'),
    [routes, fallbackRouteId],
  )
  const [state, setState] = useState<WebRuntimeState<RouteId>>(() => ({
    route: initialRoute,
    diagnostics: initialDiagnostics,
    install: {
      capability: resolveInstallCapability({
        secureContext: initialDiagnostics.secureContext,
        standalone: initialDiagnostics.standalone,
        promptAvailable: false,
      }),
      outcome: 'idle',
    },
    serviceWorker: {
      status: resolveInitialServiceWorkerStatus({
        enabled: serviceWorkerEnabled,
        supported: initialDiagnostics.serviceWorkerSupported,
      }),
    },
  }))

  const routeRef = useRef(initialRoute)
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const reloadForUpdateRef = useRef(false)

  const commitRoute = useCallback((pathname: string, navigationKind: NavigationKind) => {
    const next = createRouteState(routes, fallbackRouteId, pathname, navigationKind)
    const previous = routeRef.current
    if (previous.id === next.id && previous.pathname === next.pathname) return

    routeRef.current = next
    setState((current) => ({ ...current, route: next }))
    window.dispatchEvent(new CustomEvent<NavigationEventDetail<RouteId>>(TCHOMA_NAVIGATION_EVENT, {
      detail: { previous, current: next },
    }))
  }, [routes, fallbackRouteId])

  const navigate = useCallback((routeId: RouteId) => {
    const route = routeForId(routes, routeId)
    const pathname = normalizePathname(route.path)
    if (routeRef.current.id === routeId && routeRef.current.pathname === pathname) return
    window.history.pushState(null, '', pathname)
    commitRoute(pathname, 'push')
  }, [routes, commitRoute])

  useEffect(() => {
    const handlePopState = () => commitRoute(window.location.pathname, 'pop')
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [commitRoute])

  useEffect(() => {
    const route = routeForId(routes, state.route.id)
    if (route.title) document.title = route.title
  }, [routes, state.route.id])

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)')

    const updateEnvironment = () => {
      const diagnostics = readDiagnostics()
      setState((current) => ({
        ...current,
        diagnostics,
        install: {
          ...current.install,
          capability: resolveInstallCapability({
            secureContext: diagnostics.secureContext,
            standalone: diagnostics.standalone,
            promptAvailable: installPromptRef.current !== null,
          }),
        },
      }))
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      installPromptRef.current = event as BeforeInstallPromptEvent
      setState((current) => ({
        ...current,
        install: { capability: 'native-prompt', outcome: 'idle' },
      }))
    }

    const handleInstalled = () => {
      installPromptRef.current = null
      setState((current) => ({
        ...current,
        install: { capability: 'installed', outcome: 'accepted' },
      }))
    }

    window.addEventListener('online', updateEnvironment)
    window.addEventListener('offline', updateEnvironment)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleInstalled)
    displayMode.addEventListener('change', updateEnvironment)

    return () => {
      window.removeEventListener('online', updateEnvironment)
      window.removeEventListener('offline', updateEnvironment)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleInstalled)
      displayMode.removeEventListener('change', updateEnvironment)
      installPromptRef.current = null
    }
  }, [])

  const promptInstall = useCallback(async (): Promise<InstallPromptResult> => {
    const prompt = installPromptRef.current
    if (!prompt) return 'unavailable'

    setState((current) => ({
      ...current,
      install: { ...current.install, outcome: 'prompting', error: undefined },
    }))

    try {
      await prompt.prompt()
      const choice = await prompt.userChoice
      installPromptRef.current = null
      const outcome = choice.outcome
      const diagnostics = readDiagnostics()
      setState((current) => ({
        ...current,
        diagnostics,
        install: {
          capability: resolveInstallCapability({
            secureContext: diagnostics.secureContext,
            standalone: diagnostics.standalone,
            promptAvailable: false,
          }),
          outcome,
        },
      }))
      return outcome
    } catch (error) {
      const message = errorMessage(error)
      setState((current) => ({
        ...current,
        install: { ...current.install, outcome: 'failed', error: message },
      }))
      return 'failed'
    }
  }, [])

  useEffect(() => {
    registrationRef.current = null
    reloadForUpdateRef.current = false

    if (!serviceWorkerEnabled) {
      setState((current) => ({ ...current, serviceWorker: { status: 'disabled' } }))
      return
    }

    if (!('serviceWorker' in navigator)) {
      setState((current) => ({ ...current, serviceWorker: { status: 'unsupported' } }))
      return
    }

    let cancelled = false
    let registration: ServiceWorkerRegistration | null = null
    let observedWorker: ServiceWorker | null = null

    const setServiceWorker = (next: ServiceWorkerState) => {
      if (cancelled) return
      setState((current) => ({ ...current, serviceWorker: next }))
    }

    const handleWorkerState = () => {
      if (!observedWorker || observedWorker.state !== 'installed') return
      setServiceWorker({
        status: navigator.serviceWorker.controller ? 'update-available' : 'ready',
      })
    }

    const observeWorker = (worker: ServiceWorker | null) => {
      observedWorker?.removeEventListener('statechange', handleWorkerState)
      observedWorker = worker
      observedWorker?.addEventListener('statechange', handleWorkerState)
      handleWorkerState()
    }

    const handleUpdateFound = () => observeWorker(registration?.installing ?? null)
    const handleControllerChange = () => {
      setServiceWorker({ status: 'ready' })
      if (reloadForUpdateRef.current) window.location.reload()
    }

    const register = async () => {
      setServiceWorker({ status: 'registering' })
      try {
        registration = await navigator.serviceWorker.register(serviceWorkerUrl)
        if (cancelled) return
        registrationRef.current = registration
        registration.addEventListener('updatefound', handleUpdateFound)

        if (registration.waiting && navigator.serviceWorker.controller) {
          setServiceWorker({ status: 'update-available' })
        } else if (registration.installing) {
          observeWorker(registration.installing)
        } else {
          setServiceWorker({ status: 'ready' })
        }
      } catch (error) {
        setServiceWorker({ status: 'error', error: errorMessage(error) })
      }
    }

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
    if (document.readyState === 'complete') {
      void register()
    } else {
      window.addEventListener('load', register, { once: true })
    }

    return () => {
      cancelled = true
      window.removeEventListener('load', register)
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
      registration?.removeEventListener('updatefound', handleUpdateFound)
      observedWorker?.removeEventListener('statechange', handleWorkerState)
      registrationRef.current = null
    }
  }, [serviceWorkerEnabled, serviceWorkerUrl])

  const checkForServiceWorkerUpdate = useCallback(async () => {
    const registration = registrationRef.current
    if (!registration) return
    try {
      await registration.update()
      if (registration.waiting) {
        setState((current) => ({
          ...current,
          serviceWorker: { status: 'update-available' },
        }))
      }
    } catch (error) {
      setState((current) => ({
        ...current,
        serviceWorker: { status: 'error', error: errorMessage(error) },
      }))
    }
  }, [])

  const applyServiceWorkerUpdate = useCallback(() => {
    const waiting = registrationRef.current?.waiting
    if (!waiting) return
    reloadForUpdateRef.current = true
    waiting.postMessage({ type: 'SKIP_WAITING' })
  }, [])

  const actions = useMemo<WebRuntimeActions<RouteId>>(() => ({
    navigate,
    promptInstall,
    checkForServiceWorkerUpdate,
    applyServiceWorkerUpdate,
  }), [navigate, promptInstall, checkForServiceWorkerUpdate, applyServiceWorkerUpdate])

  const meta = useMemo<WebRuntimeMeta<RouteId>>(() => ({ routes }), [routes])
  const value = useMemo<WebRuntimeContextValue<RouteId>>(
    () => ({ state, actions, meta }),
    [state, actions, meta],
  )

  return (
    <RuntimeContext value={value as unknown as WebRuntimeContextValue<string>}>
      {children}
    </RuntimeContext>
  )
}

export function useWebRuntime<RouteId extends string = string>(): WebRuntimeContextValue<RouteId> {
  const value = use(RuntimeContext)
  if (!value) throw new Error('useWebRuntime must be used inside WebRuntimeProvider')
  return value as unknown as WebRuntimeContextValue<RouteId>
}
